import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { rolesApi, rolesQueryKeys } from '../api';
import type { RoleFilters, CreateRoleDto, UpdateRoleDto } from '../types';

// Hook for fetching roles list
export function useRoles(filters?: RoleFilters) {
  return useQuery({
    queryKey: rolesQueryKeys.list(filters),
    queryFn: () => rolesApi.getAll(filters),
  });
}

// Hook for fetching single role
export function useRole(id: number | string) {
  return useQuery({
    queryKey: rolesQueryKeys.detail(id),
    queryFn: () => rolesApi.getById(id),
    enabled: !!id,
  });
}

// Hook for creating role
export function useCreateRole() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreateRoleDto) => rolesApi.create(data),
    onSuccess: () => {
      toast.success(t('createdSuccess', { entity: t('role') }));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('createError', { error: 'Unknown error' })));
    },
  });
}

// Hook for updating role
export function useUpdateRole() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateRoleDto }) =>
      rolesApi.update({ id, data }),
    onSuccess: (_, variables) => {
      toast.success(t('updatedSuccess', { entity: t('role') }));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.detail(variables.id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}

// Hook for deleting role
export function useDeleteRole() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => rolesApi.delete(id),
    onSuccess: () => {
      toast.success(t('deletedSuccess', { entity: t('role') }));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deleteError', { error: 'Unknown error' })));
    },
  });
}

// Hook for activating role
export function useActivateRole() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => rolesApi.activate(id),
    onSuccess: () => {
      toast.success(t('roleActivated'));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}

// Hook for deactivating role
export function useDeactivateRole() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: number | string) => rolesApi.deactivate(id),
    onSuccess: () => {
      toast.success(t('roleDeactivated'));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updateError', { error: 'Unknown error' })));
    },
  });
}
